import React, { useState, useEffect } from 'react';
import { Card, Table, Alert, Button, Tag, Spin, message } from 'antd';
import { EyeInvisibleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getHiddenProgressReport } from '../../api/reports';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';

/**
 * Veliden sertifika ilerleyişi gizlenen öğrenciler.
 *
 * Bayrak sessizce kapalı kalır: çocuk kodlama sınıfına geçtiğinde birinin
 * AÇMAYI hatırlaması gerekiyor, hatırlamazsa o veli ilerleyişi bir daha hiç
 * görmez. Bu rapor o unutmayı görünür kılıyor — "Öğrenciye Git" ile doğrudan
 * kartına gidilip açılabiliyor.
 */
const HiddenProgressReportTab = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('report-hidden-progress', 20);
  const [page, setPage] = useState(1);
  const [sorter, setSorter] = useState({ field: null, order: null });

  const fetchData = (targetPage = page, perPage = persistedPageSize, sort = sorter) => {
    setLoading(true);
    getHiddenProgressReport({
      page: targetPage,
      per_page: perPage,
      sort_by: sort?.field || undefined,
      sort_direction: sort?.order === 'ascend' ? 'asc' : (sort?.order === 'descend' ? 'desc' : undefined),
    })
      .then(res => { setData(res.data); setPage(targetPage); })
      .catch(() => message.error('Rapor yüklenemedi.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(1, persistedPageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortProps = (field) => ({
    sorter: true,
    sortOrder: sorter.field === field ? sorter.order : null,
  });

  const STATUS_ETIKET = {
    active:    <Tag color="green">Aktif</Tag>,
    passive:   <Tag>Pasif</Tag>,
    candidate: <Tag color="blue">Aday</Tag>,
  };

  const columns = [
    { title: 'Öğrenci', dataIndex: 'student_name', ...sortProps('student_name') },
    { title: 'Şube', dataIndex: 'branch', ...sortProps('branch') },
    {
      title: 'Durum',
      dataIndex: 'status',
      width: 110,
      ...sortProps('status'),
      render: v => STATUS_ETIKET[v] ?? <Tag>{v}</Tag>,
    },
    {
      title: 'Son Değişiklik',
      dataIndex: 'updated_at',
      width: 140,
      ...sortProps('updated_at'),
      render: v => v ? dayjs(v).format('DD.MM.YYYY') : '—',
    },
    {
      title: '',
      key: 'git',
      width: 130,
      render: (_, r) => (
        <Button size="small" onClick={() => navigate(`/students/${r.student_id}`)}>
          Öğrenciye Git
        </Button>
      ),
    },
  ];

  if (loading && !data) return <div className="text-center p-8"><Spin /></div>;

  return (
    <div>
      <Alert
        type="info"
        showIcon
        icon={<EyeInvisibleOutlined />}
        message="Bu öğrencilerin velileri sertifika ilerleyişini görmüyor"
        description="Kazanılmış sertifikalar etkilenmez, yalnızca ilerleyiş kartı (X/Y) gizlidir. Öğrenci bir üst programa geçtiğinde ilerleyişi tekrar açmayı unutmayın."
        className="mb-4"
      />

      <Card>
        <Table
          dataSource={data?.students || []}
          rowKey="student_id"
          columns={columns}
          size="small"
          loading={loading}
          locale={{ emptyText: 'İlerleyişi gizlenen öğrenci yok.' }}
          onChange={(pag, _filters, nextSorter) => {
            const next = nextSorter?.order
              ? { field: nextSorter.field, order: nextSorter.order }
              : { field: null, order: null };
            const sortDegisti = next.field !== sorter.field || next.order !== sorter.order;
            setSorter(next);
            setPersistedPageSize(pag.pageSize);
            fetchData(sortDegisti ? 1 : pag.current, pag.pageSize, next);
          }}
          pagination={{
            current: data?.current_page,
            pageSize: data?.per_page,
            total: data?.total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total) => `Toplam ${total} öğrenci`,
          }}
        />
      </Card>
    </div>
  );
};

export default HiddenProgressReportTab;
